import {useFeatureIsOn} from "@growthbook/growthbook-react";
import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import {useCallback, useMemo} from "react";
import type {Editor} from "slate";

import {View} from "@khanacademy/wonder-blocks-core";
import {ActionItem, ActionMenu} from "@khanacademy/wonder-blocks-dropdown";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import Link from "@khanacademy/wonder-blocks-link";
import {CircularSpinner} from "@khanacademy/wonder-blocks-progress-spinner";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {publishAnalyticsEventForMinorConversion} from "@khan/analytics-learning-time";
import {useCoeditorContext} from "@khan/guide-coeditor-core";
import {useRunGuideComponent, type GuideComponents} from "@khan/guide-core";
import {getPromptVariables} from "@khan/guide-core-helpers";
import {useGuideState} from "@khan/guide-state";
import {
    clearUnsavedChanges,
    descendantsToMarkdown,
    downloadAsDocx,
    downloadAsPPTx,
    getDocumentTitle,
    printDocument,
    saveDocToGoogleDrive,
    saveFormToGoogleDrive,
    savePresenationToGoogleDrive,
    savePresentationToOneDrive,
    saveToMsOneDrive,
} from "@khan/guide-ui";
import {KAError} from "@khan/logging";
import {useLocation} from "@khan/urls";

import {
    renderErrorToast,
    renderInfoToast,
} from "#notifications-package/toast-notification.tsx";
import {
    CANCELLED_LOGIN_MESSAGE,
    INTERACTION_IN_PROGRESS_MESSAGE,
    MISSING_LICENSE_MESSAGE,
    NO_CONSENT_MESSAGE,
} from "#shared/auth/providers/microsoft/constants.ts";

import type {Tool} from "./ai-tools-list.ts";

import sparkleIcon from "@phosphor-icons/core/fill/sparkle-fill.svg";

type FailureReason =
    | "UNKNOWN"
    | "NO_LICENSE"
    | "INTERACTION_IN_PROGRESS"
    | "USER_CANCELLED"
    | "NO_CONSENT";

type Props = {
    setReferralModalOpen: (value: boolean) => void;
    tool: Pick<Tool, "configName">;
    isProvidingInputs?: boolean;
};

export default function AIToolExportDropdown(props: Props) {
    const {isProvidingInputs, setReferralModalOpen, tool} = props;
    const {editor, readOnly} = useCoeditorContext();

    const navigation = useGuideState("navigation");
    const threadId = useGuideState("threadId");

    const {href} = useLocation();

    const oneDriveIsOn = useFeatureIsOn("tx_save_to_one_drive");

    const handleToggle = useCallback(
        (opened) => {
            if (opened) {
                publishAnalyticsEventForMinorConversion({
                    eventType: "AiGuideCoeditorExportClicked",
                    eventSchemaVersion: 1,
                    aiGuideThreadId: threadId ?? "",
                    aiGuideNavigationId: navigation.id,
                    currentPageURL: href,
                });
            }
        },
        [threadId, navigation.id, href],
    );

    const showSlidesProgress = () => {
        renderInfoToast(
            <div style={styles.toast}>
                {t`Creating presentation from document`}
                <CircularSpinner light size="xsmall" style={styles.spinner} />
            </div>,
        );
    };

    const makeExportDocumentHandler = useCallback(
        (handler: (editor: Editor, ...props) => void) => {
            return (...props) => {
                if (!editor) {
                    return;
                }

                handler(editor, ...props);
                setReferralModalOpen(true);
            };
        },
        [editor, setReferralModalOpen],
    );

    const handlePrintClicked = useMemo(
        () =>
            makeExportDocumentHandler(
                (editor: Editor, useExportEvent: boolean) => {
                    if (useExportEvent) {
                        publishAnalyticsEventForMinorConversion({
                            eventType: "AiGuideCoeditorExportAsPDFSelected",
                            eventSchemaVersion: 1,
                            aiGuideThreadId: threadId ?? "",
                            aiGuideNavigationId: navigation.id,
                        });
                    } else {
                        publishAnalyticsEventForMinorConversion({
                            eventType: "AiGuideCoeditorPrintClicked",
                            eventSchemaVersion: 2,
                            aiGuideThreadId: threadId ?? "",
                            aiGuideNavigationId: navigation.id,
                            currentPageURL: href,
                        });
                    }

                    printDocument(editor);
                },
            ),
        [makeExportDocumentHandler, threadId, navigation.id, href],
    );

    const handleSaveDocClicked = useMemo(
        () =>
            makeExportDocumentHandler((editor: Editor) => {
                publishAnalyticsEventForMinorConversion({
                    eventType: "AiGuideCoeditorExportAsDocxSelected",
                    eventSchemaVersion: 1,
                    aiGuideThreadId: threadId ?? "",
                    aiGuideNavigationId: navigation.id,
                });
                downloadAsDocx(editor);
            }),
        [makeExportDocumentHandler, navigation.id, threadId],
    );

    const createSlides = useRunGuideComponent("CreateSlides");

    const handleCreateSlidesOutput = useCallback(
        async (
            editor: Editor,
            output: GuideComponents["CreateSlides"]["output"],
        ) => {
            if (output.pptType === "slides") {
                try {
                    const presUrl = await savePresenationToGoogleDrive(
                        editor,
                        output.slides,
                    );
                    renderInfoToast(
                        <View>
                            <span>{t`Presentation saved to Google Drive`}</span>
                            <Link
                                href={presUrl}
                                target="_blank"
                                style={{color: "inherit"}}
                            >
                                {t`Open presentation`}
                            </Link>
                        </View>,
                    );
                } catch (e) {
                    renderErrorToast(t`Failed to save to Google Slides`);
                    publishAnalyticsEventForMinorConversion({
                        eventType: "AiGuideCoeditorExportToGoogleSlidesFailed",
                        eventSchemaVersion: 1,
                        aiGuideThreadId: threadId ?? "",
                        aiGuideNavigationId: navigation.id,
                    });
                }
            } else if (output.pptType === "default") {
                const title = getDocumentTitle(editor);
                downloadAsPPTx(editor, title, output.slides);
            } else if (output.pptType === "one-drive") {
                try {
                    const presUrl = await savePresentationToOneDrive(
                        editor,
                        output.slides,
                    );
                    renderInfoToast(
                        <View>
                            <span>{t`Presentation saved to OneDrive`}</span>
                            <Link
                                href={presUrl}
                                target="_blank"
                                style={{color: "inherit"}}
                            >
                                {t`Open presentation`}
                            </Link>
                        </View>,
                    );
                } catch (e) {
                    const {errorMessage, failureReason} = getFailureInfo(e);

                    renderErrorToast(errorMessage);
                    publishAnalyticsEventForMinorConversion({
                        eventType: "AiGuideCoeditorExportToMsOneDriveFailed",
                        eventSchemaVersion: 2,
                        aiGuideThreadId: threadId ?? "",
                        aiGuideNavigationId: navigation.id,
                        failureReason: failureReason,
                    });
                }
            } else {
                renderErrorToast(t`Failed to save Presentation`);
                publishAnalyticsEventForMinorConversion({
                    eventType: "AiGuideCoeditorExportToGoogleSlidesFailed",
                    eventSchemaVersion: 1,
                    aiGuideThreadId: threadId ?? "",
                    aiGuideNavigationId: navigation.id,
                });
            }

            setReferralModalOpen(false);
        },
        [threadId, navigation.id, setReferralModalOpen],
    );

    const handleSlidesOptionClicked = useCallback(
        async (destination: "default" | "one-drive" | "slides") => {
            if (!editor) {
                return;
            }
            showSlidesProgress();
            clearUnsavedChanges(editor);
            const {result: markdown} = descendantsToMarkdown(editor.children);

            if (destination === "one-drive") {
                publishAnalyticsEventForMinorConversion({
                    eventType: "AiGuideCoeditorExportToMsOneDriveSelected",
                    eventSchemaVersion: 2,
                    aiGuideThreadId: threadId ?? "",
                    aiGuideNavigationId: navigation.id,
                    exportedFileType: "PPTX",
                });
            } else {
                publishAnalyticsEventForMinorConversion({
                    eventType: "AiGuideCoeditorExportToGoogleSlidesSelected",
                    eventSchemaVersion: 1,
                    aiGuideThreadId: threadId ?? "",
                    aiGuideNavigationId: navigation.id,
                });
            }

            const result = await createSlides({
                pptType: destination,
                content: markdown,
                promptVariables: getPromptVariables(),
            });

            if (result.ok) {
                handleCreateSlidesOutput(editor, result.output);
            } else {
                renderErrorToast(t`Failed to create presentation`);
                publishAnalyticsEventForMinorConversion({
                    eventType: "AiGuideCoeditorExportToGoogleSlidesFailed",
                    eventSchemaVersion: 1,
                    aiGuideThreadId: threadId ?? "",
                    aiGuideNavigationId: navigation.id,
                });
            }
        },
        [
            editor,
            navigation.id,
            createSlides,
            threadId,
            handleCreateSlidesOutput,
        ],
    );

    const handleSavePPTxClicked = useCallback(() => {
        handleSlidesOptionClicked("default");
    }, [handleSlidesOptionClicked]);

    const handleSaveToSlides = useCallback(() => {
        handleSlidesOptionClicked("slides");
    }, [handleSlidesOptionClicked]);

    const handleSaveSlidesToOneDrive = useCallback(() => {
        handleSlidesOptionClicked("one-drive");
    }, [handleSlidesOptionClicked]);

    const handleSaveToDrive = useMemo(
        () =>
            makeExportDocumentHandler(async (editor: Editor) => {
                publishAnalyticsEventForMinorConversion({
                    eventType: "AiGuideCoeditorExportToGoogleDriveSelected",
                    eventSchemaVersion: 2,
                    aiGuideThreadId: threadId ?? "",
                    aiGuideNavigationId: navigation.id,
                });

                try {
                    const docUrl = await saveDocToGoogleDrive(
                        editor,
                        getDocumentTitle(editor),
                    );
                    renderInfoToast(
                        <View>
                            <span>{t`Document saved to Google Drive`}</span>
                            <Link
                                href={docUrl}
                                target="_blank"
                                style={{color: "inherit"}}
                            >
                                {t`View document`}
                            </Link>
                        </View>,
                    );
                } catch (e) {
                    renderErrorToast(t`Failed to save to Google Drive`);
                    publishAnalyticsEventForMinorConversion({
                        eventType: "AiGuideCoeditorExportToGoogleDriveFailed",
                        eventSchemaVersion: 1,
                        aiGuideThreadId: threadId ?? "",
                        aiGuideNavigationId: navigation.id,
                    });
                }
            }),
        [makeExportDocumentHandler, threadId, navigation.id],
    );

    const createForm = useRunGuideComponent("CreateForm");

    const handleCreateFormOutput = useCallback(
        async (
            editor: Editor,
            output: GuideComponents["CreateForm"]["output"],
        ) => {
            try {
                const formUrl = await saveFormToGoogleDrive(
                    tool.configName,
                    output.form,
                    getDocumentTitle(editor),
                );
                renderInfoToast(
                    <View>
                        <span>{t`Saved to Google Drive`}</span>
                        <Link
                            href={formUrl}
                            target="_blank"
                            style={{color: "inherit"}}
                        >
                            {t`View`}
                        </Link>
                    </View>,
                );
                clearUnsavedChanges(editor);
            } catch (e) {
                renderErrorToast(t`Failed to save to Google Drive`);

                publishAnalyticsEventForMinorConversion({
                    eventType: "AiGuideCoeditorExportToGoogleFormFailed",
                    eventSchemaVersion: 1,
                    aiGuideThreadId: threadId ?? "",
                    aiGuideNavigationId: navigation.id,
                });
            }
        },
        [navigation.id, threadId, tool.configName],
    );

    const handleSaveForm = useCallback(async () => {
        publishAnalyticsEventForMinorConversion({
            eventType: "AiGuideCoeditorExportToGoogleFormSelected",
            eventSchemaVersion: 1,
            aiGuideThreadId: threadId ?? "",
            aiGuideNavigationId: navigation.id,
        });

        if (!editor) {
            return;
        }

        clearUnsavedChanges(editor);

        const {result: document} = descendantsToMarkdown(editor.children);

        const result = await createForm({
            document: document,
        });

        if (result.ok) {
            handleCreateFormOutput(editor, result.output);
        } else {
            renderErrorToast(t`Failed to save to Google Forms`);

            publishAnalyticsEventForMinorConversion({
                eventType: "AiGuideCoeditorExportToGoogleFormFailed",
                eventSchemaVersion: 1,
                aiGuideThreadId: threadId ?? "",
                aiGuideNavigationId: navigation.id,
            });
        }
    }, [createForm, editor, handleCreateFormOutput, navigation.id, threadId]);

    const handleSaveToMSOneDrive = useMemo(
        () =>
            makeExportDocumentHandler(async (editor: Editor) => {
                publishAnalyticsEventForMinorConversion({
                    eventType: "AiGuideCoeditorExportToMsOneDriveSelected",
                    eventSchemaVersion: 2,
                    aiGuideThreadId: threadId ?? "",
                    aiGuideNavigationId: navigation.id,
                    exportedFileType: "DOCX",
                });

                try {
                    const fileURL = await saveToMsOneDrive(editor);
                    renderInfoToast(
                        <View>
                            <span>{t`Document saved to OneDrive`}</span>
                            <Link
                                href={fileURL}
                                target="_blank"
                                style={{color: "inherit"}}
                            >
                                {t`View document`}
                            </Link>
                        </View>,
                    );
                } catch (e) {
                    const {errorMessage, failureReason} = getFailureInfo(e);

                    renderErrorToast(errorMessage);
                    publishAnalyticsEventForMinorConversion({
                        eventType: "AiGuideCoeditorExportToMsOneDriveFailed",
                        eventSchemaVersion: 2,
                        aiGuideThreadId: threadId ?? "",
                        aiGuideNavigationId: navigation.id,
                        failureReason: failureReason,
                    });
                }
            }),
        [makeExportDocumentHandler, threadId, navigation.id],
    );

    const externalButtonDisabled = !editor || readOnly || isProvidingInputs;

    const exportSlidesSupported =
        tool.configName === "teacher-tool-create-lesson-plan" ||
        tool.configName === "teacher-tool-exit-ticket";

    const ActionItems = useMemo(() => {
        const keyPrefix = "export-dropdown-option";
        const items = [
            <ActionItem
                key={`${keyPrefix}-1`}
                onClick={handleSaveDocClicked}
                label={t`Microsoft Word (.docx)`}
                style={{paddingRight: spacing.large_24}}
            />,
            <ActionItem
                key={`${keyPrefix}-2`}
                onClick={() => handlePrintClicked(true)}
                label={t`PDF Document (.pdf)`}
            />,
            <ActionItem
                key={`${keyPrefix}-3`}
                onClick={handleSaveToDrive}
                label={t`Save to Google Drive`}
            />,
            <ActionItem
                key={`${keyPrefix}-4`}
                onClick={handleSaveToMSOneDrive}
                label={t`Save to Microsoft OneDrive`}
            />,
        ];

        if (exportSlidesSupported) {
            items.push(
                <ActionItem
                    key={`${keyPrefix}-5`}
                    onClick={handleSavePPTxClicked}
                    label={t`PowerPoint (.pptx)`}
                    rightAccessory={
                        <PhosphorIcon
                            icon={sparkleIcon}
                            size="small"
                            color={color.eggplant}
                        />
                    }
                />,
            );

            items.push(
                <ActionItem
                    key={`${keyPrefix}-6`}
                    onClick={handleSaveToSlides}
                    label={t`Save to Google Slides`}
                    rightAccessory={
                        <PhosphorIcon
                            icon={sparkleIcon}
                            size="small"
                            color={color.eggplant}
                        />
                    }
                />,
            );
            if (oneDriveIsOn) {
                // hide this option until string is translated
                items.push(
                    <ActionItem
                        key={`${keyPrefix}-7`}
                        onClick={handleSaveSlidesToOneDrive}
                        label={t`Microsoft OneDrive (.pptx)`}
                        rightAccessory={
                            <PhosphorIcon
                                icon={sparkleIcon}
                                size="small"
                                color={color.eggplant}
                            />
                        }
                    />,
                );
            }
        }

        if (
            tool.configName === "teacher-tool-multiple-choice-quiz" ||
            tool.configName === "teacher-tool-exit-ticket" ||
            tool.configName === "teacher-tool-questions-generator"
        ) {
            items.push(
                <ActionItem
                    key={`${keyPrefix}-3`}
                    onClick={handleSaveForm}
                    label={t`Save to Google Forms`}
                    rightAccessory={
                        <PhosphorIcon
                            icon={sparkleIcon}
                            size="small"
                            color={color.eggplant}
                        />
                    }
                />,
            );
        }

        return items;
    }, [
        handleSaveDocClicked,
        handleSaveToDrive,
        oneDriveIsOn,
        exportSlidesSupported,
        handlePrintClicked,
        handleSaveToMSOneDrive,
        handleSavePPTxClicked,
        handleSaveSlidesToOneDrive,
        handleSaveToSlides,
        handleSaveForm,
        tool.configName,
    ]);

    return (
        <ActionMenu
            menuText={t`Export`}
            disabled={externalButtonDisabled}
            onToggle={(opened) => handleToggle(opened)}
            alignment="right"
            style={styles.button}
        >
            {ActionItems}
        </ActionMenu>
    );
}

function getFailureInfo(e: any): {
    errorMessage: string;
    failureReason: FailureReason;
} {
    let errorMessage = t`Failed to save to OneDrive`;
    let failureReason: FailureReason = "UNKNOWN";
    if (e instanceof KAError) {
        switch (e.message) {
            case MISSING_LICENSE_MESSAGE:
                errorMessage = t`Failed to save to OneDrive: User's Microsoft Tenant does not have access to OneDrive`;
                failureReason = "NO_LICENSE";
                break;
            case INTERACTION_IN_PROGRESS_MESSAGE:
                errorMessage = t`Login interaction already in progress. Please use the open popup.`;
                failureReason = "INTERACTION_IN_PROGRESS";
                break;
            case CANCELLED_LOGIN_MESSAGE:
                failureReason = "USER_CANCELLED";
                break;
            case NO_CONSENT_MESSAGE:
                failureReason = "NO_CONSENT";
                break;
            default:
                break; // empty default because it is the same as when e is not a KAError
        }
    }

    return {errorMessage, failureReason};
}

const styles = StyleSheet.create({
    button: {
        marginLeft: spacing.medium_16,
    },
    spinner: {
        display: "inline-block",
        verticalAlign: "middle",
        paddingLeft: spacing.medium_16 + "px",
    },
    toast: {textAlign: "center"},
});
